import httpService from "./httpService";

export interface JobExecution {
  id: string;
  jobName: string;
  executionName: string;
  status: "PENDING" | "RUNNING" | "SUCCEEDED" | "FAILED" | "CANCELLED";
  createdDatetimeUtc: string;
  updatedDatetimeUtc: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  triggerType: string;
  triggeredBy: string;
  errorMessage: string | null;
  errorCode: string | null;
  retryCount: number;
  jobMetadata: Record<string, unknown> | null;
}

export interface JobExecutionResponse {
  message: string;
  execution: {
    name: string;
    uid: string;
    generation: string;
    labels: Record<string, string>;
    annotations: Record<string, string>;
    createTime: string;
    startTime: string | null;
    completionTime: string | null;
    conditions: any[];
  };
}

export interface JobNamesResponse {
  message: string;
  jobNames: string[];
}

export interface JobExecutionsSearchResponse {
  message: string;
  count: number;
  executions: JobExecution[];
}

export interface JobExecutionCountResponse {
  message: string;
  status: string;
  count: number;
}

export interface CleanupResponse {
  message: string;
  timePeriod: string;
  deletedCount: number;
}

export interface ExecuteStagesResponse {
  message: string;
  triggered: boolean;
  stageCount: number;
  execution?: JobExecutionResponse;
}

class JobService {
  private static instance: JobService;

  private constructor() {}

  public static getInstance(): JobService {
    if (!JobService.instance) {
      JobService.instance = new JobService();
    }
    return JobService.instance;
  }

  /**
   * Get all available job names
   */
  public async getJobNames(): Promise<string[]> {
    const response = await httpService.get<JobNamesResponse>("/v2/admin/jobs/names");
    return response.data.jobNames;
  }

  /**
   * Run a job
   */
  public async runJob(jobName: string, envVars: { name: string; value: string }[] = []): Promise<JobExecutionResponse> {
    const response = await httpService.post<JobExecutionResponse>("/v2/admin/jobs/run", {
      jobName,
      envVars,
    });
    return response.data;
  }

  /**
   * Cancel a job execution
   */
  public async cancelJob(jobName: string, executionName: string): Promise<JobExecutionResponse> {
    const response = await httpService.post<JobExecutionResponse>("/v2/admin/jobs/cancel", {
      jobName,
      executionName,
    });
    return response.data;
  }

  /**
   * Search job executions
   */
  public async searchJobExecutions(params: {
    id?: string;
    jobName?: string;
    executionName?: string;
    status?: string;
  }): Promise<JobExecutionsSearchResponse> {
    // Filter out undefined params
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value);
    });

    const queryString = queryParams.toString();
    const url = `/v2/admin/job-executions/search${queryString ? `?${queryString}` : ""}`;
    
    const response = await httpService.get<JobExecutionsSearchResponse>(url);
    return response.data;
  }

  /**
   * Get job execution count
   */
  public async getJobExecutionCount(status?: string): Promise<JobExecutionCountResponse> {
    const url = status 
      ? `/v2/admin/job-executions/count?status=${status}`
      : "/v2/admin/job-executions/count";
      
    const response = await httpService.get<JobExecutionCountResponse>(url);
    return response.data;
  }

  /**
   * Get a specific job execution by ID
   */
  public async getJobExecution(id: string): Promise<JobExecution> {
    const response = await httpService.get<{ message: string; execution: JobExecution }>(
      `/v2/admin/job-executions/${id}`
    );
    return response.data.execution;
  }

  /**
   * Delete completed job executions
   */
  public async cleanupJobExecutions(timePeriod: string): Promise<CleanupResponse> {
    const response = await httpService.delete<CleanupResponse>(
      `/v2/admin/job-executions/cleanup?timePeriod=${timePeriod}`
    );
    return response.data;
  }

  /**
   * Trigger the rebalance-order-job for a specific user stage
   * @param userStageId - The ID of the user stage to process
   */
  public async triggerUserStageJob(userStageId: number): Promise<JobExecutionResponse> {
    const envVars = [
      { name: "REBALANCE_EVENT_USER_STAGE_ID", value: String(userStageId) },
      { name: "FORCE", value: "false" },
    ];
    return this.runJob("rebalance-order-job", envVars);
  }

  /**
   * Trigger the rebalance-execute-trades-job for a rebalance event.
   * Uses the dedicated endpoint that initializes stage summaries before job run.
   */
  public async executeStagesForRebalanceEvent(
    rebalanceEventId: number
  ): Promise<ExecuteStagesResponse> {
    const response = await httpService.post<ExecuteStagesResponse>(
      "/v2/admin/jobs/execute-stages-for-rebalance-event",
      { rebalanceEventId }
    );
    return response.data;
  }

  /**
   * Trigger the rebalance-trade-status-job for a rebalance event.
   * Fetches trade execution details (commission, fills) from IBKR for all completed stages.
   * Should be called after stages are completed to populate commission data.
   * @param rebalanceEventId - The ID of the rebalance event
   */
  public async triggerTradeStatusJob(rebalanceEventId: number): Promise<JobExecutionResponse> {
    const envVars = [
      { name: "REBALANCE_EVENT_ID", value: String(rebalanceEventId) },
    ];
    return this.runJob("rebalance-trade-status-job", envVars);
  }
}

export const jobService = JobService.getInstance();
export default jobService;
